import { memo, useState, useRef, useEffect } from 'react';
import { Flex, Box, Switch } from '@chakra-ui/react';
import { LuPencil, LuCamera } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import type { MenuContentItem, MenuContentBadgeDef } from './types';
import { EDIT_HOVER, EDIT_TRANSITION, formatPrice } from './types';
import type { MenuTheme } from '../../api/menus';
import InlineEdit, { deriveBorder } from './InlineEdit';

interface MenuItemProps {
  item: MenuContentItem;
  theme: MenuTheme;
  currency: string;
  isEditMode: boolean;
  badgeDefinitions?: MenuContentBadgeDef[];
  soldOutLabel: string;
  onItemClick?: (id: string) => void;
  onImageClick?: (url: string) => void;
  onQuickEdit?: (id: string, updates: { name?: string; description?: string; price?: number; is_visible?: boolean; is_sold_out?: boolean }) => void;
  onQuickEditImage?: (id: string) => void;
}

const PriceEdit = ({ value, currency, color, textColor, onSave }: { value: number; currency: string; color: string; textColor: string; onSave: (price: number) => void }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));
  const inputRef = useRef<HTMLInputElement>(null);
  const border = deriveBorder(textColor);

  useEffect(() => { setDraft(String(value)); }, [value]);
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const save = () => {
    setIsEditing(false);
    const parsed = parseFloat(draft);
    if (!isNaN(parsed) && parsed >= 0 && parsed !== value) {
      onSave(parsed);
    } else {
      setDraft(String(value));
    }
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={e => {
          e.stopPropagation();
          if (e.key === 'Enter') { e.preventDefault(); inputRef.current?.blur(); }
          if (e.key === 'Escape') { setDraft(String(value)); setIsEditing(false); }
        }}
        onClick={e => e.stopPropagation()}
        style={{
          fontSize: '13px',
          fontWeight: 500,
          color,
          background: 'transparent',
          border: `1px dashed ${border.active}`,
          borderRadius: '4px',
          outline: 'none',
          width: '80px',
          padding: '2px 4px',
          fontFamily: 'inherit',
          boxSizing: 'border-box',
        }}
      />
    );
  }

  return (
    <Box
      onClick={(e) => { e.stopPropagation(); setDraft(String(value)); setIsEditing(true); }}
      cursor="text"
      fontSize="13px"
      fontWeight="500"
      color={color}
      borderWidth="1px"
      borderStyle="dashed"
      borderColor={border.idle}
      borderRadius="4px"
      px="4px"
      mx="-4px"
      transition="border-color 0.15s, background-color 0.15s"
      _hover={{ borderColor: border.hover }}
    >
      {formatPrice(value, currency)}
    </Box>
  );
};

const QuickToggle = ({ label, checked, onChange }: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <Flex alignItems="center" gap="6px" onClick={(e) => e.stopPropagation()}>
    <Switch.Root size="sm" checked={checked} onCheckedChange={(e) => onChange(e.checked)}>
      <Switch.HiddenInput />
      <Switch.Control><Switch.Thumb /></Switch.Control>
    </Switch.Root>
    <Box fontSize="11px" fontWeight="500" color="#999999">{label}</Box>
  </Flex>
);

const MenuItem = memo(({
  item,
  theme: mt,
  currency,
  isEditMode,
  badgeDefinitions,
  soldOutLabel,
  onItemClick,
  onImageClick,
  onQuickEdit,
  onQuickEditImage,
}: MenuItemProps) => {
  const { t } = useTranslation();
  const isQuickEditMode = !!onQuickEdit;

  return (
    <Flex
      gap="12px"
      py={isEditMode ? undefined : '10px'}
      p={isEditMode ? '8px' : undefined}
      mx={isEditMode ? '-8px' : undefined}
      borderRadius={isEditMode ? '8px' : undefined}
      borderWidth={isEditMode ? '1px' : undefined}
      borderStyle={isEditMode ? 'solid' : undefined}
      borderBottomWidth={isEditMode ? undefined : '1px'}
      borderColor={isEditMode ? 'transparent' : mt.border_color}
      cursor={isEditMode ? 'pointer' : undefined}
      transition={isEditMode ? EDIT_TRANSITION : undefined}
      _hover={isEditMode ? EDIT_HOVER : undefined}
      opacity={item.is_sold_out ? 0.5 : 1}
      onClick={isEditMode && onItemClick ? () => onItemClick(item.id) : undefined}
      css={isEditMode ? { '&:hover .edit-icon': { opacity: 1 } } : undefined}
      position="relative"
    >
      <Box
        width="160px"
        height="160px"
        borderRadius="8px"
        overflow="hidden"
        flexShrink={0}
        backgroundColor={!item.image_url ? mt.border_color : undefined}
        cursor={isQuickEditMode || (!isEditMode && item.image_url) ? 'pointer' : undefined}
        position="relative"
        onClick={
          isQuickEditMode && onQuickEditImage
            ? (e) => { e.stopPropagation(); onQuickEditImage(item.id); }
            : !isEditMode && item.image_url
              ? (e) => { e.stopPropagation(); onImageClick?.(item.image_url!); }
              : undefined
        }
        css={isQuickEditMode ? { '&:hover .quick-edit-image-overlay': { opacity: 1 } } : undefined}
      >
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Flex height="100%" justifyContent="center" alignItems="center">
            <Box width="32px" height="32px" borderRadius="6px" backgroundColor={mt.secondary_text_color} opacity={0.2} />
          </Flex>
        )}
        {isQuickEditMode && (
          <Flex
            className="quick-edit-image-overlay"
            position="absolute"
            inset="0"
            justifyContent="center"
            alignItems="center"
            backgroundColor="blackAlpha.500"
            opacity={0}
            transition="opacity 0.15s"
            borderRadius="8px"
          >
            <Flex backgroundColor="blackAlpha.600" borderRadius="full" p="8px" color="white">
              <LuCamera size={16} />
            </Flex>
          </Flex>
        )}
      </Box>

      <Flex direction="column" flex="1" justifyContent="space-between" minWidth={0}>
        <Box>
          <Flex alignItems="center" gap="6px" flexWrap="wrap">
            {isQuickEditMode ? (
              <Box flex="1" minWidth={0}>
                <InlineEdit
                  value={item.name}
                  onSave={(name) => onQuickEdit(item.id, { name })}
                  fontSize="15px"
                  fontWeight="600"
                  color={mt.text_color}
                />
              </Box>
            ) : (
              <Box fontSize="15px" fontWeight="600" color={mt.text_color}>
                {item.name}
              </Box>
            )}
            {(item.badges || []).map((badgeKey) => {
              const def = badgeDefinitions?.find(b => b.key === badgeKey);
              return (
                <Box
                  key={badgeKey}
                  fontSize="10px"
                  fontWeight="500"
                  color={def?.color || (isEditMode ? '#FF6B35' : mt.secondary_text_color)}
                  backgroundColor={def ? `${def.color}18` : (isEditMode ? '#FFF3ED' : mt.border_color)}
                  px="6px"
                  py="1px"
                  borderRadius="4px"
                >
                  {def?.label || badgeKey}
                </Box>
              );
            })}
            {item.is_sold_out && (
              <Box fontSize="10px" fontWeight="500" color="#CC3333" backgroundColor="#FFEEEE" px="6px" py="1px" borderRadius="4px">
                {soldOutLabel}
              </Box>
            )}
            {isEditMode && item.is_visible === false && (
              <Box fontSize="10px" fontWeight="500" color="#999999" backgroundColor="#F0F0F0" px="6px" py="1px" borderRadius="4px">
                {t('menus.hidden')}
              </Box>
            )}
          </Flex>
          {isQuickEditMode ? (
            <Box mt="2px">
              <InlineEdit
                value={item.description || ''}
                onSave={(description) => onQuickEdit(item.id, { description })}
                fontSize="12px"
                color={mt.secondary_text_color}
                lineHeight="150%"
                multiline
                required={false}
                placeholder="Add description..."
              />
            </Box>
          ) : item.description ? (
            <Box
              fontSize="12px"
              color={mt.secondary_text_color}
              lineHeight="150%"
              mt="2px"
              overflow="hidden"
              css={{
                display: '-webkit-box',
                WebkitLineClamp: item.options && item.options.length > 0 ? Math.max(1, 5 - item.options.length) : 5,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {item.description}
            </Box>
          ) : null}
        </Box>
        {item.options && item.options.length > 0 && (
          <Flex direction="column" gap="3px" mt="4px" alignItems="flex-start">
            {item.options.map((opt, i) => (
              <Box
                key={i}
                fontSize="11px"
                fontWeight="400"
                color={mt.secondary_text_color}
                backgroundColor={mt.border_color}
                px="6px"
                py="1px"
                borderRadius="4px"
              >
                {opt.name}{opt.price > 0 ? ` +${formatPrice(opt.price, currency)}` : ''}
              </Box>
            ))}
          </Flex>
        )}
        <Flex alignItems="center" justifyContent="space-between" mt={item.options && item.options.length > 0 ? '0px' : '4px'}>
          {isQuickEditMode ? (
            <PriceEdit
              value={item.price}
              currency={currency}
              color={mt.price_color}
              textColor={mt.text_color}
              onSave={(price) => onQuickEdit(item.id, { price })}
            />
          ) : (
            <Box fontSize="13px" fontWeight="500" color={mt.price_color}>
              {formatPrice(item.price, currency)}
            </Box>
          )}
          {item.spicy_level != null && item.spicy_level > 0 && (
            <Flex gap="1px" alignItems="center">
              {Array.from({ length: item.spicy_level }).map((_, i) => (
                <Box key={i} fontSize="12px" lineHeight="1">{'\uD83C\uDF36\uFE0F'}</Box>
              ))}
            </Flex>
          )}
        </Flex>
        {isQuickEditMode && (
          <Flex gap="12px" mt="6px">
            <QuickToggle
              label={t('menus.visible', 'Visible')}
              checked={item.is_visible !== false}
              onChange={(checked) => onQuickEdit(item.id, { is_visible: checked })}
            />
            <QuickToggle
              label={soldOutLabel}
              checked={item.is_sold_out}
              onChange={(checked) => onQuickEdit(item.id, { is_sold_out: checked })}
            />
          </Flex>
        )}
      </Flex>
      {isEditMode && (
        <Box className="edit-icon" opacity={0} transition="opacity 0.15s" color={mt.secondary_text_color} flexShrink={0} alignSelf="center">
          <LuPencil size={14} />
        </Box>
      )}
    </Flex>
  );
});

export default MenuItem;
